import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Spinner, Tabs, TabsList, TabsTrigger } from '@ibsys/kit';
import { RenessansOperatorsResponse } from '@/entities/renessans-dashboards';
import { formatMMSS } from './utils';

/** Режим сравнения: отдельные операторы или их группы. */
export type ComparisonMode = 'operator' | 'group';

type Props = {
  data: RenessansOperatorsResponse | undefined;
  isLoading: boolean;
  mode: ComparisonMode;
  onChangeMode: (mode: ComparisonMode) => void;
};

/** Один элемент для отображения в диаграмме сравнения. */
type ComparisonItem = {
  id: number;
  name: string;
  avgDuration: number | null;
};

/**
 * Виджет 6 «Сравнение времени разговора»: горизонтальная столбчатая
 * диаграмма средней длительности разговора по операторам или группам.
 * Переключатель режима без мультиселекторов — показываются все элементы
 * текущего среза. В режиме групп бэкенд фильтрует по подстроке «ренессанс»
 * (параметр зашит на странице).
 */
export const ComparisonWidget: FC<Props> = ({ data, isLoading, mode, onChangeMode }) => {
  const { t } = useTranslation('renessansDashboard');

  const items = useMemo<ComparisonItem[]>(() => {
    if (!data) return [];
    if (mode === 'group') {
      return (data.groups ?? []).map((g) => ({
        id: g.groupId,
        name: g.groupName,
        avgDuration: g.avgDuration,
      }));
    }
    return (data.operators ?? []).map((o) => ({
      id: o.operatorId,
      name: o.operatorName,
      avgDuration: o.avgDuration,
    }));
  }, [data, mode]);

  const sortedItems = useMemo(() => [...items].sort((a, b) => (b.avgDuration ?? 0) - (a.avgDuration ?? 0)), [items]);

  const maxValue = Math.max(...sortedItems.map((i) => i.avgDuration ?? 0), 1);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle>{t('comparison.title')}</CardTitle>
        {isLoading && <Spinner className="size-4" />}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Tabs
            value={mode}
            onValueChange={(value) => {
              if (value === 'operator' || value === 'group') {
                onChangeMode(value);
              }
            }}
          >
            <TabsList>
              <TabsTrigger value="operator">{t('comparison.modeOperator')}</TabsTrigger>
              <TabsTrigger value="group">{t('comparison.modeGroup')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {sortedItems.length === 0 && <div className="text-muted-foreground text-sm">{t('noData')}</div>}
        {sortedItems.length > 0 && (
          <div className="flex flex-col gap-2">
            {sortedItems.map((item) => {
              const value = item.avgDuration ?? 0;
              const widthPct = (value / maxValue) * 100;
              return (
                <div key={item.id} className="grid grid-cols-[minmax(100px,180px)_1fr_70px] gap-2 items-center">
                  <span className="text-sm truncate">{item.name}</span>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${Math.max(2, widthPct)}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-right">{formatMMSS(item.avgDuration)}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
